// Ch83 · can we text this person, and if not, WHY not — decided before the
// button is drawn.
//
// Grant, after reading the inbound card: "yes we havent used the texting much
// or at all really". The card said it exactly: 10 outbound texts in the
// system's entire history, one reply ever. The pipe was never broken; the loop
// was seven steps across two devices, ending in a "Log Text" button that
// records the past instead of doing the thing.
//
// So the work list gets a real Send. The rule that makes it trustworthy is
// this: NEVER OFFER A SEND THE SERVER WILL REFUSE. Both endpoints already
// refuse for good reasons, and an operator who clicks Send and gets a 400 twice
// stops trusting the button — which is how you get back to ten texts a year.
//
// The refusals, from the routes themselves:
//   · no phone                        both routes, 400
//   · lead not verified               /leads/{id}/sms, 400 — the 10DLC
//                                     reputation gate; we do not text someone
//                                     the verify pipeline has not passed
//   · client consent not granted      /clients/{id}/sms, via outbound_guardrails
//
// A lead and a client take DIFFERENT endpoints, which is why this returns the
// route as well as the verdict: the work list holds both, and picking the wrong
// one is a 404 rather than a refusal.

import type { WorkRow } from "./leadsWork";

export type SendRoute = "lead" | "client";

export interface Sendability {
  canSend: boolean;
  /** Which endpoint this person's row belongs to. */
  route: SendRoute;
  /** The id for that route — a lead id or a client id, never mixed. */
  id: string | null;
  /** Why not, in the operator's words. Empty when canSend. */
  reason: string;
  /** What would make it sendable — actionable, not a restatement. */
  remedy: string;
}

function phoneOf(row: WorkRow): string | null {
  const raw = row.person.kind === "client"
    ? (row.person.client as { phone?: string | null } | undefined)?.phone
    : row.person.lead?.phone;
  const trimmed = (raw || "").trim();
  return trimmed || null;
}

/**
 * Who is asking. PKT-0666 · #540 shipped without this, and the omission was the
 * defect: sendability() mirrored the phone and 10DLC predicates and never asked
 * whether this user is ALLOWED to send. On production every locator with role
 * `agent` saw a live Send on every row and got a 403 from all of them — the
 * exact thing #540's own rule forbids.
 */
export interface Sender {
  /** The viewer's profile id, compared against clients.assigned_agent_id. */
  id: string | null;
  /** "admin" | "manager" | "agent" | "demo" — lowercased by the caller or here. */
  role: string | null;
  /**
   * Is SMS_AGENT_SEND_ENABLED on for this deploy? `null` = we could not find
   * out (older backend, API unreachable). Unknown is NOT permission: an agent
   * gets a named refusal rather than a button that might 403.
   */
  agentSendEnabled: boolean | null;
}

const ELEVATED = new Set(["admin", "manager"]);

/**
 * PKT-0666 §F1 · this lead must be claimed before anyone can text it.
 *
 * Grant ruled "assigned only", and a lead has no owner until it is claimed, so
 * first contact on a brand-new lead would otherwise be impossible — the Leads
 * page's whole job. The Director ruled the literal form of his words: the row's
 * Send becomes ONE CLICK that claims (migration 049's atomic RPC, which also
 * converts the lead into a client assigned to the claimer) and then opens the
 * composer.
 *
 * Note what this is NOT: it is not a permission check. Whether the claim
 * succeeds is the server's answer, and it can refuse — somebody else may claim
 * the same lead a second earlier, and a lead with no name or email cannot be
 * converted at all (`lead_missing_required_identity`). The caller surfaces both.
 */
export function needsClaimBeforeTexting(row: WorkRow): boolean {
  return row.person.kind === "lead" && row.stage === "unclaimed";
}

/**
 * Would this person be textable by THIS sender once claimed?
 *
 * Deliberately NOT `sendability()`. That asks about the LEAD route, which is
 * manager/admin and has no assignment concept — the wrong question here,
 * because claiming converts the lead into a CLIENT assigned to the claimer, and
 * the client route is what governs afterwards. Asking the lead question would
 * hide the shortcut from the locator it helps most.
 *
 * So: the resulting client is theirs by construction (049 sets
 * assigned_agent_id to the claimer), which leaves the agent flag as the only
 * other gate, plus a phone to send to. A locator who can claim but not text
 * should be offered a plain Claim, not a promise of a message.
 */
export function canClaimThenText(row: WorkRow, sender: Sender): boolean {
  if (!needsClaimBeforeTexting(row)) return false;
  if (!phoneOf(row)) return false;
  // The id check comes FIRST, ABOVE the elevated short-circuit. 049 claims TO
  // the caller (`assigned_agent_id = v_owner`), so a caller with no id would
  // claim the lead to nobody, and the text this button promises could never be
  // sent. That is as true of an admin as of an agent — being elevated is not an
  // id. It read correctly for `agent` only because `ELEVATED.has(role)`
  // returned before the check could run.
  if (!(sender.id || "").trim()) return false;
  const role = (sender.role || "").toLowerCase();
  if (ELEVATED.has(role)) return true;
  return sender.agentSendEnabled === true;
}

export function sendability(row: WorkRow, sender: Sender): Sendability {
  const isClient = row.person.kind === "client";
  const route: SendRoute = isClient ? "client" : "lead";
  // `?? null` alone lets an empty string through, and `""` is not "no id" to a
  // caller — it is a request to /leads//sms. Found by mutation.
  const rawId = isClient ? row.person.client?.id : row.person.lead?.id;
  const id = (rawId || "").trim() || null;
  const base = { route, id };

  if (!id) {
    return { ...base, canSend: false, reason: "No record to send from", remedy: "Open the record" };
  }
  // ---- WHO MAY SEND ------------------------------------------------------
  // Mirrors the routes, which are the only real gate (the API holds the
  // service_role key, so RLS cannot see a send at all):
  //   · POST /leads/{id}/sms      require_manager_or_admin. No assignment
  //                               concept — a lead HAS no owner column;
  //                               migration 033 dropped it and ownership lives
  //                               in the lead_claims ledger.
  //   · POST /clients/{id}/sms    manager/admin always; an agent only when
  //                               SMS_AGENT_SEND_ENABLED is on AND they are in
  //                               the client's assigned set.
  // Checked BEFORE phone and verification on purpose: if this is not your
  // client, "no phone number" is not your problem and not your next action.
  const role = (sender.role || "").toLowerCase();
  const elevated = ELEVATED.has(role);

  if (!isClient) {
    // LEAD route · require_manager_or_admin, and no assignment concept exists:
    // a lead has no owner column (028 added one, 033 dropped it) and ownership
    // lives in the lead_claims ledger.
    if (!elevated) {
      return {
        ...base,
        canSend: false,
        reason: "Leads are texted by a manager",
        remedy: "Claim this lead to your book first, then text them as a client",
      };
    }
  } else {
    // CLIENT route · PKT-0666 D2, Grant's ruling: "assigned only", and it binds
    // EVERY role. An admin texting a client assigned to someone else is a 403
    // now — we all share one Twilio number, so the renter sees one conversation
    // and two people sending into it is the thing this closes.
    //
    // The agent flag applies ON TOP for agents only; it never granted an admin
    // anything and does not now.
    if (!elevated && sender.agentSendEnabled !== true) {
      return {
        ...base,
        canSend: false,
        reason: "Texting is not enabled for your account",
        remedy: "An admin turns this on — ask before texting from here",
      };
    }
    const owner = (row.person.agentId || "").trim();
    if (!owner || owner !== (sender.id || "").trim()) {
      return {
        ...base,
        canSend: false,
        reason: owner ? "Assigned to someone else" : "Not assigned to anyone",
        remedy: owner
          ? "We all share one number, so the assigned locator sends this one"
          : "Assign this client to a locator, and they can text them",
      };
    }
  }

  if (!phoneOf(row)) {
    return {
      ...base,
      canSend: false,
      reason: "No phone number",
      remedy: "Add a phone to the record",
    };
  }
  if (!isClient) {
    // The 10DLC gate. `verification_provider` is what the route checks — the
    // presence of a scorer run, not its verdict: an ULTRA_FAKE lead is still
    // textable, it has just been through the pipeline. Mirroring the server's
    // ACTUAL predicate rather than a stricter guess is the whole point; a UI
    // that refused on tier would hide sends the server would have allowed.
    const verified = Boolean((row.person.lead as { verification_provider?: string | null } | undefined)?.verification_provider);
    if (!verified) {
      return {
        ...base,
        canSend: false,
        reason: "Not verified yet",
        remedy: "Verify this lead first",
      };
    }
  }
  return { ...base, canSend: true, reason: "", remedy: "" };
}

/**
 * The first line of a message to this person, offered as a starting point.
 *
 * NOT auto-sent and not clever. A locator texting a stranger about an apartment
 * writes roughly the same opener every time, and having to retype it is one of
 * the seven steps. It is fully editable — the draft is a convenience, never a
 * claim about what they should say.
 */
export function draftOpener(row: WorkRow, senderName?: string | null): string {
  const first = (row.person.name || "").trim().split(/\s+/)[0] || "there";
  const from = (senderName || "").trim().split(/\s+/)[0];
  const sig = from ? ` — ${from} at LocateNow` : " — LocateNow";
  if (row.stage === "unclaimed" || row.stage === "claimed_uncontacted") {
    return `Hi ${first}, thanks for reaching out about apartments! Do you still need a place, and what's your move-in date?${sig}`;
  }
  if (row.stage === "converted_uncontacted") {
    return `Hi ${first}, I'm helping with your apartment search. Want me to send a few places that fit?${sig}`;
  }
  return `Hi ${first}, just checking in on your apartment search — still looking?${sig}`;
}

/**
 * Did the text actually LEAVE?
 *
 * Ch83 · found in review of #540, and it is the defect that would have undone
 * the whole feature. `send_sms` is documented "Never raises" — a timeout, an
 * HTTP error from the carrier, missing credentials, or scaffold mode all come
 * back as a normal SmsResult, the route persists the attempt and answers
 * **HTTP 200**. The first version of the composer treated any non-throwing
 * response as success: it closed, said nothing, and the operator walked away
 * believing a text had gone that never left the building.
 *
 * That is strictly worse than the seven-step loop it replaced. In the old loop
 * the locator watched the message send on their own phone.
 *
 * The two routes report this differently, so the predicate is per route:
 *
 *   LEAD · `/leads/{id}/sms` returns SendSmsResponse, which carries
 *     `skipped_reason` and the persisted row's `twilio_sid`. The estate rule
 *     already written down for this table: a NULL twilio_sid means Twilio was
 *     never called. It is also exactly the predicate lead_flow_truth uses for
 *     the funnel's `contacted` step, so the screen and the funnel agree on what
 *     a send is.
 *
 *   CLIENT · `/clients/{id}/sms` returns ClientThreadMessage, which does NOT
 *     expose twilio_sid — `status` is the only signal, set server-side to
 *     "failed" whenever a skipped_reason was present.
 *
 * Scaffold mode counts as NOT LEFT on purpose. It returns ok=true with
 * status="scaffold" and no sid; nothing touches the wire. Production runs live,
 * but if that env ever flips, the honest failure is a visible "it did not send"
 * rather than a UI cheerfully reporting delivery of nothing.
 */
export type SendOutcome = { left: true } | { left: false; why: string };

const SKIPPED_REASON_COPY: Record<string, string> = {
  timeout: "The carrier did not respond in time. Nothing was sent.",
  http_error: "The carrier rejected the request. Nothing was sent.",
  missing_credentials: "Texting is not configured on the server. Nothing was sent.",
  no_recipient: "The server had no phone number for them. Nothing was sent.",
  empty_body: "The server received an empty message. Nothing was sent.",
};

export function sendOutcome(route: SendRoute, response: unknown): SendOutcome {
  const res = (response ?? {}) as Record<string, unknown>;

  if (res.status === 'outcome_unknown' || res.skipped_reason === 'outcome_unknown') {
    return { left: false, why: 'Send outcome unknown — check before resending. Refresh the thread for a provider update.' };
  }
  if (route === "lead") {
    const skipped = typeof res.skipped_reason === "string" ? res.skipped_reason.trim() : "";
    if (skipped) {
      return {
        left: false,
        why: SKIPPED_REASON_COPY[skipped] ?? `The provider refused it (${skipped}). Nothing was sent.`,
      };
    }
    const message = (res.message ?? {}) as Record<string, unknown>;
    const sid = typeof message.twilio_sid === "string" ? message.twilio_sid.trim() : "";
    if (!sid) {
      return {
        left: false,
        why: "The server recorded the message but never reached the carrier. Nothing was sent.",
      };
    }
    return { left: true };
  }

  const status = typeof res.status === "string" ? res.status.trim().toLowerCase() : "";
  if (status === "failed" || status === "scaffold") {
    return {
      left: false,
      why: "The server recorded the message but never reached the carrier. Nothing was sent.",
    };
  }
  return { left: true };
}
